export function normalizeSearchText(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

export function scoreTextMatch(text: string, query: string, weight: number): number {
    const normalizedText = normalizeSearchText(text);
    const normalizedQuery = normalizeSearchText(query);

    if (!normalizedText || !normalizedQuery) {
        return 0;
    }

    if (normalizedText === normalizedQuery) {
        return weight * 12;
    }

    if (normalizedText.startsWith(normalizedQuery)) {
        return weight * 10 - normalizedText.length;
    }

    if (normalizedText.includes(normalizedQuery)) {
        return weight * 8 - normalizedText.length;
    }

    const tokenPrefixScore = scoreTokenPrefixMatch(normalizedText, normalizedQuery, weight);
    const acronymScore = scoreAcronymMatch(normalizedText, normalizedQuery, weight);
    const subsequenceScore = scoreSubsequenceMatch(normalizedText, normalizedQuery, weight);

    return Math.max(tokenPrefixScore, acronymScore, subsequenceScore, 0);
}

function scoreTokenPrefixMatch(text: string, query: string, weight: number): number {
    const tokens = text.split(' ').filter(Boolean);
    const queryTokens = query.split(' ').filter(Boolean);

    if (!tokens.length || !queryTokens.length) {
        return 0;
    }

    const everyTokenMatched = queryTokens.every(queryToken =>
        tokens.some(token => token.startsWith(queryToken) || scoreSubsequence(token, queryToken) > 0)
    );

    if (!everyTokenMatched) {
        return 0;
    }

    return weight * 6 - text.length;
}

function scoreAcronymMatch(text: string, query: string, weight: number): number {
    const acronym = text
        .split(' ')
        .filter(Boolean)
        .map(token => token[0])
        .join('');

    if (!acronym) {
        return 0;
    }

    const subsequence = scoreSubsequence(acronym, query);
    return subsequence > 0 ? weight * 5 + subsequence : 0;
}

function scoreSubsequenceMatch(text: string, query: string, weight: number): number {
    const subsequence = scoreSubsequence(text.replace(/\s+/g, ''), query.replace(/\s+/g, ''));
    return subsequence > 0 ? weight * 4 + subsequence - text.length : 0;
}

function scoreSubsequence(text: string, query: string): number {
    if (!text || !query) {
        return 0;
    }

    let textIndex = 0;
    let previousMatchIndex = -2;
    let score = 0;

    for (const char of query) {
        let matched = false;

        while (textIndex < text.length) {
            if (text[textIndex] === char) {
                score += previousMatchIndex === textIndex - 1 ? 3 : 1;
                previousMatchIndex = textIndex;
                textIndex += 1;
                matched = true;
                break;
            }
            textIndex += 1;
        }

        if (!matched) {
            return 0;
        }
    }

    return score;
}
